import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete, dvGet } from '../api/dataverseClient';
import { RFP, RFPStatus } from '../types';

const FIELDS = [
  'dagrun_pm_rfpid', 'dagrun_pm_name', 'dagrun_pm_rfpnumber', 'dagrun_pm_clientname',
  'dagrun_pm_duedate', 'dagrun_pm_submissionduedate',
  'dagrun_pm_status', 'dagrun_pm_side', 'dagrun_pm_estimatedvalue',
  'dagrun_pm_ownerid', 'dagrun_pm_ownername', 'dagrun_pm_projectid', 'dagrun_pm_notes',
  'dagrun_pm_description', 'dagrun_pm_gonobiddecision',
  'dagrun_pm_linkedprojectid', 'dagrun_pm_linkedprojectname',
  'dagrun_pm_crmdealid', 'dagrun_pm_crmaccountid',
  'createdon',
];

/**
 * Tenant-wide list of RFPs ordered by submission due date (soonest
 * first), with submission-due nulls falling to the bottom. Sorting
 * client-side because Dataverse cannot do a custom NULLs-LAST sort
 * without a synthetic computed column.
 */
export async function listRFPs(pca: IPublicClientApplication): Promise<RFP[]> {
  const rows = await dvList<RFP>(pca, 'dagrun_pm_rfps', {
    select: FIELDS,
    orderby: 'createdon desc',
    top: 500,
  });
  return rows;
}

export async function getRFP(pca: IPublicClientApplication, id: string): Promise<RFP> {
  return dvGet<RFP>(pca, 'dagrun_pm_rfps', id, { select: FIELDS });
}

export interface CreateRFPInput {
  name: string;
  rfpnumber?: string | null;
  clientname?: string | null;
  duedate?: string | null;
  submissionduedate?: string | null;
  status?: number;
  side?: number;
  estimatedvalue?: number | null;
  ownerid?: string | null;
  ownername?: string | null;
  notes?: string | null;
  description?: string | null;
}

export async function createRFP(pca: IPublicClientApplication, input: CreateRFPInput): Promise<string> {
  const body: Record<string, unknown> = { dagrun_pm_name: input.name };
  if (input.rfpnumber)            body.dagrun_pm_rfpnumber = input.rfpnumber;
  if (input.clientname)           body.dagrun_pm_clientname = input.clientname;
  if (input.duedate)              body.dagrun_pm_duedate = input.duedate;
  if (input.submissionduedate)    body.dagrun_pm_submissionduedate = input.submissionduedate;
  body.dagrun_pm_status = input.status ?? RFPStatus.Identified;
  body.dagrun_pm_side   = input.side ?? 1;
  if (input.estimatedvalue != null) body.dagrun_pm_estimatedvalue = input.estimatedvalue;
  if (input.ownerid)              body.dagrun_pm_ownerid = input.ownerid;
  if (input.ownername)            body.dagrun_pm_ownername = input.ownername;
  if (input.notes)                body.dagrun_pm_notes = input.notes;
  if (input.description)          body.dagrun_pm_description = input.description;
  return dvCreate(pca, 'dagrun_pm_rfps', body);
}

export async function updateRFP(pca: IPublicClientApplication, id: string, patch: Record<string, unknown>): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_rfps', id, patch);
}

export async function deleteRFP(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_rfps', id);
}
